'use client';

import { c as _c } from "react/compiler-runtime";
import type { Route } from 'next';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { toast } from 'sonner';
import { AI_REVIEW_TABS } from '@/lib/constants';
import { suppressKeyWithFunc } from '@/lib/stores/keyboard-shortcuts/keyboard-shortcuts';
type GlobalKeyboardShortcutsProviderProps = {
  children: React.ReactNode;
};

// Keyed by `KeyboardEvent.code`, which react-hotkeys-hook matches keys on too.
const GO_TO_PATHS: Partial<Record<string, Route>> = {
  KeyI: '/inbox',
  KeyS: '/settings',
  KeyR: '/ai-review',
  KeyC: `/ai-review?tab=${AI_REVIEW_TABS.CUSTOM_RULES}`
};
const GO_TO_SEQUENCE_TIMEOUT_MS = 1000;
function isTextField(target: EventTarget | null) {
  return target instanceof HTMLElement && (target.isContentEditable || target.matches('input, textarea, select'));
}

/**
 * Navigation shortcuts typed as "G then I", "G then S", "G then R" and "G then C". A key pressed
 * while G is still held counts too. The listener runs in the capture phase on window, ahead of
 * react-hotkeys-hook's listeners on document, so the second key never also fires a single-key
 * shortcut such as I (installation select) or R (review menu).
 */
function useGoToShortcuts() {
  const $ = _c(3);
  const router = useRouter();
  let t0;
  let t1;
  if ($[0] !== router) {
    t0 = () => {
      let goPressedAt = null;
      const handleKeyDown = event => {
        const hasModifier = event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
        if (hasModifier || event.isComposing || isTextField(event.target)) {
          goPressedAt = null;
          return;
        }
        if (event.code === "KeyG") {
          goPressedAt = Date.now();
          return;
        }
        const isSequence = goPressedAt !== null && Date.now() - goPressedAt <= GO_TO_SEQUENCE_TIMEOUT_MS;
        goPressedAt = null;
        const path = isSequence ? GO_TO_PATHS[event.code] : undefined;
        if (!path) {
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        router.push(path);
      };
      window.addEventListener("keydown", handleKeyDown, {
        capture: true
      });
      return () => {
        window.removeEventListener("keydown", handleKeyDown, {
          capture: true
        });
      };
    };
    t1 = [router];
    $[0] = router;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
}
export function GlobalKeyboardShortcutsProvider(t0) {
  const $ = _c(6);
  const {
    children
  } = t0;
  const router = useRouter();
  useGoToShortcuts();
  let t1;
  if ($[0] !== router) {
    t1 = () => router.push("/settings");
    $[0] = router;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      preventDefault: true,
      enableOnFormTags: false
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useHotkeys("mod+comma", t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = suppressKeyWithFunc("c", _temp);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleCopyURL = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      preventDefault: true,
      enableOnFormTags: true
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  useHotkeys("mod+shift+c", handleCopyURL, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      enableOnFormTags: false
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  useHotkeys("c+l", handleCopyURL, t5);
  return children;
}
function _temp() {
  navigator.clipboard.writeText(window.location.href);
  toast.success("URL copied to clipboard");
}